/**
 * BreadcrumbContext — single source of truth for the global top-bar path and
 * the controls that sit at its right-hand end.
 *
 * Pages call `useBreadcrumb(segments)` to declare their position in the app.
 * Each segment: { label: string, to?: string }
 *   - If `to` is set the segment is a clickable Link (parent pages).
 *   - The last segment is always plain text (current page / resource).
 *
 * Editor pages (e.g. InsightsEditorPage) call `useHideGlobalTopBar(true)` to
 * collapse the standalone breadcrumb bar so AnalyticsTopBar can render one
 * unified row containing both the path and action buttons.
 *
 * OWNERSHIP. There is one top bar and several components that can speak for
 * it, and they overlap in time: the Ask panel opens beside a page, a drawer
 * opens over one. So each slot is a stack of owners rather than a single
 * value. The newest owner is shown; when it leaves, the one underneath comes
 * back. Passing `null` means "not mine" and claims nothing at all.
 *
 * That is not a detail. When this was one value with `setActions(null)` on
 * unmount, opening Ask on the Traces page blanked the Conversations / Runs /
 * Spans tabs, and closing it left them blank for good: the page's own effect
 * had already run and had no reason to run again.
 *
 * Example:
 *   useBreadcrumb([
 *     { label: 'Runs', to: `/${team}/agent-analytics/runs` },
 *     { label: run?.agent_name || runId },
 *   ]);
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

const BreadcrumbContext = createContext({
  segments: [],
  claimSegments: () => {},
  releaseSegments: () => {},
  topBarHidden: false,
  pushHide: () => {},
  popHide: () => {},
  actions: null,
  claimActions: () => {},
  releaseActions: () => {},
});

/** Stable per-component-instance key, so an owner can be found again. */
let nextOwnerId = 0;
function useOwnerId() {
  const ref = useRef(null);
  if (ref.current === null) {
    nextOwnerId += 1;
    ref.current = nextOwnerId;
  }
  return ref.current;
}

/**
 * Add or update an owner's entry without disturbing the order. An owner whose
 * value changes keeps its place in the stack — only mounting and unmounting
 * move things — so a page updating its own breadcrumb cannot jump over a
 * drawer that opened on top of it.
 */
function withOwner(stack, id, value) {
  const i = stack.findIndex((e) => e.id === id);
  if (i === -1) return [...stack, { id, value }];
  if (stack[i].value === value) return stack;
  const next = stack.slice();
  next[i] = { id, value };
  return next;
}

function withoutOwner(stack, id) {
  return stack.some((e) => e.id === id) ? stack.filter((e) => e.id !== id) : stack;
}

const EMPTY_SEGMENTS = [];

const topValue = (stack, fallback) => (stack.length ? stack[stack.length - 1].value : fallback);

export function BreadcrumbProvider({ children }) {
  const [segmentStack, setSegmentStack] = useState([]);
  const [actionStack, setActionStack] = useState([]);
  const [hideCount, setHideCount] = useState(0);

  const claimSegments = useCallback((id, segments) => setSegmentStack((s) => withOwner(s, id, segments)), []);
  const releaseSegments = useCallback((id) => setSegmentStack((s) => withoutOwner(s, id)), []);
  const claimActions = useCallback((id, node) => setActionStack((s) => withOwner(s, id, node)), []);
  const releaseActions = useCallback((id) => setActionStack((s) => withoutOwner(s, id)), []);
  // Counted, not a boolean: two pages can want the bar hidden at once (an
  // editor with a drawer over it), and the first one to leave must not
  // uncover it while the other is still there.
  const pushHide = useCallback(() => setHideCount((n) => n + 1), []);
  const popHide = useCallback(() => setHideCount((n) => Math.max(0, n - 1)), []);

  const segments = topValue(segmentStack, EMPTY_SEGMENTS);
  const actions = topValue(actionStack, null);

  const value = useMemo(
    () => ({
      segments,
      claimSegments,
      releaseSegments,
      topBarHidden: hideCount > 0,
      pushHide,
      popHide,
      actions,
      claimActions,
      releaseActions,
    }),
    [segments, actions, hideCount, claimSegments, releaseSegments, claimActions, releaseActions, pushHide, popHide],
  );
  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/**
 * Call this at the top of any page/route component.
 * Pass a stable array — typically built inline with useMemo or derived from props.
 * The effect runs whenever any label or `to` changes.
 *
 * `null` means "this component does not own the breadcrumb" — the Ask panel
 * renders beside a page that has already set its own.
 *
 * @param {Array<{label:string, to?:string}>|null} segments
 */
export function useBreadcrumb(segments) {
  const { claimSegments, releaseSegments } = useContext(BreadcrumbContext);
  const id = useOwnerId();

  // Compared by content, not identity, so callers can pass an inline array.
  const json = JSON.stringify(segments ?? null);
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  useEffect(() => {
    if (segmentsRef.current == null) {
      releaseSegments(id);
      return;
    }
    claimSegments(id, segmentsRef.current);
  }, [json, id, claimSegments, releaseSegments]);

  // Separate from the update above so a change of value never re-orders the
  // stack — only leaving does.
  useEffect(() => () => releaseSegments(id), [id, releaseSegments]);
}

/**
 * Call inside a component that wants to collapse the standalone GlobalTopBar
 * so it can render its own unified breadcrumb+actions row.
 * Pass `shouldHide = true` to hide, `false` (or omit) to leave it visible.
 *
 * @param {boolean} shouldHide
 */
export function useHideGlobalTopBar(shouldHide = true) {
  const { pushHide, popHide } = useContext(BreadcrumbContext);
  useEffect(() => {
    if (!shouldHide) return undefined;
    pushHide();
    return () => popHide();
  }, [shouldHide, pushHide, popHide]);
}

export function useBreadcrumbSegments() {
  return useContext(BreadcrumbContext).segments;
}

export function useTopBarHidden() {
  return useContext(BreadcrumbContext).topBarHidden;
}

/**
 * Inject a ReactNode into the GlobalTopBar's right-side actions slot.
 * Call this at the top of any page that needs a primary action button
 * sitting inline with the breadcrumb path (e.g. "Add cohort", "New run").
 *
 * Pass `null` to claim nothing — a component that only sometimes owns the
 * slot (the chat page does, its panel form does not) says so this way rather
 * than taking the slot and rendering nothing into it.
 *
 * @param {React.ReactNode} node
 */
export function useBreadcrumbActions(node) {
  const { claimActions, releaseActions } = useContext(BreadcrumbContext);
  const id = useOwnerId();

  // Keep a live ref so the stable wrapper always renders the latest node
  // without needing it in any dependency array (avoids infinite loops when
  // callers pass inline JSX that produces a new object every render).
  const nodeRef = useRef(node);
  nodeRef.current = node;
  const owns = node != null;

  // The injected ActionsSlot lives under GlobalTopBar — a different subtree —
  // so it does NOT re-render when the calling page re-renders. For static
  // action buttons that's fine, but pages with live content in the slot
  // (counts, async-loaded dropdown options, controlled inputs) would freeze
  // at first-render values. We bridge the two subtrees: the page pings a
  // forceUpdate after every commit so ActionsSlot re-reads the latest node.
  const pingRef = useRef(null);
  useEffect(() => {
    if (pingRef.current) pingRef.current();
  }); // no deps → runs after every render of the calling page

  useEffect(() => {
    if (!owns) {
      releaseActions(id);
      return undefined;
    }
    // Stable forwarder — mounted once per claim. It registers its own
    // forceUpdate so the page can drive its re-renders, and reads the live ref
    // each time so labels, counts, and handlers are always current.
    function ActionsSlot() {
      const [, forceUpdate] = useReducer((c) => c + 1, 0);
      pingRef.current = forceUpdate;
      useEffect(() => () => { pingRef.current = null; }, []);
      return nodeRef.current ?? null;
    }
    claimActions(id, <ActionsSlot />);
    return undefined;
  }, [owns, id, claimActions, releaseActions]);

  useEffect(() => () => releaseActions(id), [id, releaseActions]);
}

export function useBreadcrumbActionsNode() {
  return useContext(BreadcrumbContext).actions;
}
